import { EstablishmentAPIPromiseClient } from "../../../gen/witbooking/backoffice/property/v1/establishment_api_grpc_web_pb";
import { AmenityAPIPromiseClient } from "../../../gen/witbooking/backoffice/amenities/v1/amenity_api_grpc_web_pb";
import {
  GetPropertyRequest,
  Location,
  Multimedia,
  Property,
  PropertyTranslation,
  UpdatePropertyRequest,
} from "../../../gen/witbooking/backoffice/property/v1/establishment_api_pb";
import { GetAmenitiesRequest } from "../../../gen/witbooking/backoffice/amenities/v1/amenity_api_pb";
import { addAuthzMetadataGrpcCall, buildGrpcClient } from "../../../lib/grpc";
import { Image } from "../../shared/widgets/ImageManager";
import { DEFAULT_TIME_ZONE, PropertyEntity } from "../domain/property.entity";
import { SelectOption } from "../components/PropertyAmenitiesSelector";

const grpcClient: EstablishmentAPIPromiseClient = buildGrpcClient(EstablishmentAPIPromiseClient);
const amenityApiClient: AmenityAPIPromiseClient = buildGrpcClient(AmenityAPIPromiseClient);

function translationDtoFromEntity(obj: PropertyTranslation.AsObject): PropertyTranslation {
  const result = new PropertyTranslation();
  result.setLocale(obj.locale);
  result.setName(obj.name);
  result.setDescription(obj.description);
  return result;
}

function multimediaDtoFromEntity(obj: Image): Multimedia {
  const result = new Multimedia();
  result.setId(obj.id);
  result.setFilename(obj.filename);
  result.setUrl(obj.url);
  result.setLabelsList([]);
  return result;
}
function imageFromMultimedia(multimedia: Multimedia.AsObject): Image | undefined {
  if (multimedia.filename === "empty.png") {
    return undefined;
  }
  return Object.assign(new File([""], multimedia.filename), {
    id: multimedia.id,
    url: multimedia.url,
    filename: multimedia.filename,
  });
}

function locationDtoFromEntity(obj: PropertyEntity): Location | undefined {
  if (!obj) {
    return undefined;
  }
  const result = new Location();
  result.setCountry(obj.country);
  result.setCity(obj.city);
  result.setAddress(obj.address);
  result.setZipCode(obj.zipCode);
  result.setLatitude(obj.latitude);
  result.setLongitude(obj.longitude);
  result.setTimeZone(obj.timeZone);
  return result;
}
function dtoFromEntity(obj: PropertyEntity): Property {
  const result = new Property();
  result.setId(obj.id);
  result.setTicker(obj.ticker);
  result.setPropertyType(obj.propertyType);
  result.setValuation(obj.valuation);
  result.setLocation(locationDtoFromEntity(obj));
  result.setPhone(obj.phone);
  result.setAdminEmail(obj.adminEmail);
  result.setContactEmail(obj.contactEmail);
  result.setTranslationsList(obj.translationsList.map(translationDtoFromEntity));
  result.setMultimediaList(obj.multimediaList.map(multimediaDtoFromEntity));
  result.setAmenitiesList(Object.keys(obj.amenitiesList ?? []));
  return result;
}

export async function getEntity(): Promise<PropertyEntity> {
  const request = new GetPropertyRequest();
  const getProperty = addAuthzMetadataGrpcCall(grpcClient, grpcClient.getProperty);
  const response = await getProperty(request);
  const entity = response.getProperty();
  if (!entity) {
    throw new Error("Unexpected reply obtaining property");
  }
  const { location, multimediaList = [], amenitiesList = [], ...obj } = entity.toObject();
  if (!location) {
    throw new Error("Property location cannot be undefined");
  }
  const images = multimediaList.map(imageFromMultimedia).filter((it): it is Image => Boolean(it));
  const selectedAmenities = amenitiesList.reduce<PropertyEntity["amenitiesList"]>(
    (acc, ticker) => ({ ...acc, [ticker]: { value: ticker } }),
    {}
  );

  return {
    ...obj,
    ...location,
    timeZone: location.timeZone || DEFAULT_TIME_ZONE,
    multimediaList: images,
    amenitiesList: selectedAmenities,
  };
}

export async function updateEntity(entity: PropertyEntity): Promise<void> {
  const oldEntity = await getEntity();
  const newEntity: PropertyEntity = {
    ...entity,
    multimediaList: oldEntity.multimediaList,
  };
  const request = new UpdatePropertyRequest();
  request.setProperty(dtoFromEntity(newEntity));
  const updateProperty = addAuthzMetadataGrpcCall(grpcClient, grpcClient.updateProperty);
  await updateProperty(request);
}

export async function getAvailableAmenities(): Promise<SelectOption[]> {
  const getAmenities = addAuthzMetadataGrpcCall(amenityApiClient, amenityApiClient.getAmenities);
  const response = await getAmenities(new GetAmenitiesRequest());
  return response.getAmenitiesList().map(amenity => ({
    value: amenity.getTicker(),
    icon: amenity.getIcon() || undefined,
    translations: amenity.getTranslationsList().map(t => ({
      locale: t.getLocale(),
      name: t.getName(),
    })),
  }));
}
